import React, { useRef, useEffect } from "react";
import ChipGroup from "../../../components/select/ChipGroup";
import ChipButton from "../../../components/select/ChipButton";
import RangeSlider from "../../../components/input/RangeSlider";
import RangeInputPair from "../../../components/input/RangeInputPair";
import UnitToggle from "../../../components/select/UnitToggle";
import HorizontalScrollContainer from "./HorizontalScrollContainer";
import { buildFilterSections } from "../constants/filterSections";
import styles from "./SingleFilterModal.module.scss";

/**
 * 개별 필터 모달
 *
 * 피그마: 지도 위 칩바에서 개별 칩 클릭 시 열리는 모달
 * - 제목바: 필터명 + "중복선택 가능" + X 닫기
 * - 필터 콘텐츠
 * - 하단 액션바 없음 (선택 즉시 반영)
 */

// 필터 ID → 모달 제목 + 부제
const FILTER_META = {
  estateType: { title: "매물유형" },
  dealType: { title: "거래유형", subtitle: "중복선택 가능" },
  price: { title: "가격" },
  area: { title: "면적" },
  approval: { title: "사용승인일" },
  useArea: { title: "용도지역", subtitle: "중복선택 가능" },
  floor: { title: "층수", subtitle: "중복선택 가능" },
  availableDate: { title: "입주 가능일" },
  interior: { title: "내부구조" },
  direction: { title: "방향", subtitle: "중복선택 가능" },
};

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// 섹션별 초기화할 키 매핑
const SECTION_RESET_KEYS = {
  estateType: ["randType", "selectedCategory"],
  dealType: ["deal_type"],
  price: ["minPrice", "maxPrice", "minDeposit", "maxDeposit", "minMonthly", "maxMonthly"],
  area: ["minArea", "maxArea", "exclusiveAreaOnly"],
  approval: ["approvalYear"],
  useArea: ["useArea"],
  floor: ["floorType"],
  availableDate: ["availableDate"],
  interior: ["roomCnt", "bathroomCnt"],
  direction: ["direction"],
};

const RESET_VALUES = {
  randType: [], selectedCategory: null,
  deal_type: [],
  minPrice: null, maxPrice: null, minDeposit: null, maxDeposit: null, minMonthly: null, maxMonthly: null,
  minArea: null, maxArea: null, areaUnit: "pyeong", exclusiveAreaOnly: false,
  approvalYear: null, useArea: [], floorType: [], availableDate: [], roomCnt: [], bathroomCnt: [], direction: [],
};

const SingleFilterModal = ({
  filterId,
  filter,
  onChange,
  onClose,
  selectedCategory,
}) => {
  const meta = FILTER_META[filterId] || { title: filterId };
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);

  // 모달 내부에서 mousedown → mouseup까지 드래그 상태 추적
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onDown = () => { isDraggingRef.current = true; };
    const onUp = () => { setTimeout(() => { isDraggingRef.current = false; }, 0); };
    container.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    return () => {
      container.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // 외부 클릭 시 닫기 (드래그 중이면 무시)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isDraggingRef.current) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [onClose]);

  // buildFilterSections에서 해당 섹션의 필터 정의를 가져옴
  const sections = buildFilterSections({ selectedCategory });
  const section = sections.find((s) => s.id === filterId);

  if (!section) return null;

  const hasReset = ["price", "area", "useArea", "interior"].includes(filterId);

  return (
    <div className={styles.container} ref={containerRef}>
      {/* 제목바 */}
      <div className={styles.titleBar}>
        <div className={styles.titleGroup}>
          <span className={styles.titleText}>{meta.title}</span>
          {meta.subtitle && (
            <span className={styles.subtitle}>{meta.subtitle}</span>
          )}
        </div>
        <button type="button" className={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      {/* 필터 콘텐츠 */}
      <div className={styles.content}>
        {section.filters.map((f, idx) => {
          const visible =
            f.visible == null
              ? true
              : typeof f.visible === "function"
                ? f.visible(filter)
                : f.visible;
          if (!visible) return null;

          return (
            <React.Fragment key={f.id}>
              {/* 피그마: range 필터 간 0.5px thin divider */}
              {idx > 0 && f.type === "range" && (
                <div className={styles.thinDivider} />
              )}
              <div className={styles.filterBlock}>
                {renderSingleFilter(f, filter, onChange)}
              </div>
            </React.Fragment>
          );
        })}

        {/* 초기화 버튼 — 가격, 면적, 용도지역, 내부구조만 */}
        {hasReset && (
          <div className={styles.resetBar}>
            <button
              type="button"
              className={styles.resetButton}
              onClick={() => {
                const keys = SECTION_RESET_KEYS[filterId] || [];
                keys.forEach((k) => onChange(k, RESET_VALUES[k]));
              }}
            >
              <ResetIcon />
              <span>초기화</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function renderSingleFilter(f, filter, onChange) {
  switch (f.type) {
    case "category-tab": {
      // 물건유형 — 전체필터와 동일한 구조 (카테고리 탭 바 + 하위 칩)
      const selectedCategoryValue = filter.selectedCategory;
      const selectedCategoryIdx = selectedCategoryValue != null
        ? f.categories.findIndex((cat) => cat.value === selectedCategoryValue)
        : -1;
      const selectedCat = selectedCategoryIdx >= 0 ? f.categories[selectedCategoryIdx] : null;

      // randType은 "17,19,12" 같은 평탄화된 쉼표 구분 문자열
      const valueArray = (() => {
        const v = filter[f.id];
        if (!v || (Array.isArray(v) && v.length === 0)) return [];
        if (typeof v === "string") return v.split(",").map((s) => s.trim()).filter(Boolean);
        return Array.isArray(v) ? v.map(String) : [];
      })();

      // subOption value가 "10,11" 같은 복합값일 수 있으므로 — 구성 타입이 모두 포함된 경우 선택됨
      const selectedSubOptionValues = selectedCat
        ? selectedCat.subOptions
            .filter((opt) => {
              const parts = String(opt.value).split(",").map((s) => s.trim());
              return parts.length > 0 && parts.every((p) => valueArray.includes(p));
            })
            .map((opt) => opt.value)
        : [];

      const handleCategoryClick = (idx) => {
        const newIdx = selectedCategoryIdx === idx ? -1 : idx;
        const categoryValue = newIdx >= 0 ? f.categories[newIdx].value : null;
        onChange("selectedCategory", categoryValue);
        // 카테고리 클릭 시 해당 카테고리 서브항목 전체 자동 선택
        if (newIdx >= 0) {
          onChange(f.id, f.categories[newIdx].subOptions.map((o) => o.value));
        } else {
          onChange(f.id, []);
        }
      };

      return (
        <div className={styles.categoryTabFilter}>
          {/* 카테고리 탭 바 — HorizontalScrollContainer로 좌우 스크롤 */}
          <HorizontalScrollContainer className={styles.categoryTabs}>
            <button
              type="button"
              className={`${styles.categoryTab} ${selectedCategoryIdx === -1 ? styles.categoryTabActive : ""}`}
              onClick={() => handleCategoryClick(-1)}
            >
              전체
            </button>
            {f.categories.map((cat, idx) => (
              <button
                key={cat.value}
                type="button"
                className={`${styles.categoryTab} ${selectedCategoryIdx === idx ? styles.categoryTabActive : ""}`}
                onClick={() => handleCategoryClick(idx)}
              >
                {cat.label}
              </button>
            ))}
          </HorizontalScrollContainer>

          {/* 하위 칩 */}
          <div className={styles.categoryChips}>
            {selectedCat ? (
              <ChipGroup
                options={selectedCat.subOptions}
                value={
                  // 카테고리 내 전체 선택 시 "전체" 버튼 active 표시를 위해 빈 배열로 변환 (서브옵션 2개 이상일 때만)
                  selectedCat.subOptions.length > 1 && selectedSubOptionValues.length >= selectedCat.subOptions.length ? [] : selectedSubOptionValues
                }
                onChange={(val) => {
                  // 빈 배열 = 카테고리 내 전체 → 해당 카테고리의 모든 값으로 설정
                  if (Array.isArray(val) && val.length === 0) {
                    onChange(f.id, selectedCat.subOptions.map((o) => o.value));
                  } else {
                    onChange(f.id, val);
                  }
                }}
                multiple
                showAll={selectedCat.subOptions.length > 1}
                size="md"
              />
            ) : (
              <ChipGroup
                options={[{ value: "__all__", label: "전체유형" }]}
                value={["__all__"]}
                onChange={() => {}}
                multiple={false}
                size="md"
              />
            )}
          </div>
        </div>
      );
    }

    case "chip":
      return (
        <div className={styles.chipArea}>
          <ChipGroup
            options={f.options}
            value={filter[f.id]}
            onChange={(val) => onChange(f.id, val)}
            multiple={f.multiple}
            showAll={f.showAll}
            size="md"
          />
        </div>
      );

    case "grouped-chip": {
      const value = filter[f.id] || [];
      const allEmpty = value.length === 0;

      return (
        <div className={styles.groupedChips}>
          {/* 최상단 "전체" */}
          <div className={styles.groupedChipTopSection}>
            <div className={styles.groupedChipArea}>
              <ChipButton
                label="전체"
                selected={allEmpty}
                size="md"
                onClick={() => onChange(f.id, [])}
              />
            </div>
            <div className={styles.groupedChipDivider} />
          </div>

          {/* 그룹별 */}
          {f.groups.map((group) => {
            const groupVals = group.options.map((o) => String(o.value));
            const groupSelected = value.filter((v) => groupVals.includes(String(v)));
            // "전체" active = 그룹 항목 전부 명시적으로 선택됨 (미선택과 구분)
            const allGroupSelected = group.options.length > 0 && groupSelected.length === group.options.length;

            const handleGroupAllClick = () => {
              const withoutGroup = value.filter((v) => !groupVals.includes(String(v)));
              onChange(f.id, [...withoutGroup, ...group.options.map((o) => o.value)]);
            };

            const handleOptionClick = (optValue) => {
              const withoutGroup = value.filter((v) => !groupVals.includes(String(v)));
              if (allGroupSelected) {
                // 전체 상태에서 개별 클릭 → 그 항목만 선택
                onChange(f.id, [...withoutGroup, optValue]);
              } else {
                const isSelected = groupSelected.some((v) => String(v) === String(optValue));
                const next = isSelected
                  ? groupSelected.filter((v) => String(v) !== String(optValue))
                  : [...groupSelected, optValue];
                // 모든 항목 선택 시 전체 상태로 전환
                if (next.length >= group.options.length) {
                  onChange(f.id, [...withoutGroup, ...group.options.map((o) => o.value)]);
                } else {
                  onChange(f.id, [...withoutGroup, ...next]);
                }
              }
            };

            return (
              <div key={group.label} className={styles.groupedChipGroup}>
                <div className={styles.groupedChipGroupLabel}>{group.label}</div>
                <div className={styles.groupedChipGroupBody}>
                  <div className={styles.groupedChipArea}>
                    <div className={styles.groupedChipChips}>
                      <ChipButton
                        label="전체"
                        selected={allGroupSelected}
                        size="md"
                        onClick={handleGroupAllClick}
                      />
                      {group.options.map((opt) => (
                        <ChipButton
                          key={opt.value}
                          label={opt.label}
                          selected={!allGroupSelected && groupSelected.some((v) => String(v) === String(opt.value))}
                          size="md"
                          onClick={() => handleOptionClick(opt.value)}
                          disabled={opt.disabled}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={styles.groupedChipDivider} />
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    case "range": {
      const unitToggleOptions = f.unitToggle;
      const unitIndex = unitToggleOptions
        ? unitToggleOptions.findIndex((u) => u.value === filter[f.unitKey])
        : -1;
      const activeUnit = unitToggleOptions
        ? unitToggleOptions[Math.max(0, unitIndex)]
        : null;

      const rangeConfig = activeUnit?.rangeConfig || f;
      const unit = activeUnit?.label || f.unit;
      const format = activeUnit?.format || f.format;
      const parse = activeUnit?.parse || f.parse;

      return (
      <>
        <div className={styles.rangeBlock}>
          {(f.rangeTitle || unitToggleOptions) && (
            <div className={styles.rangeHeader}>
              {f.rangeTitle && <span className={styles.rangeTitle}>{f.rangeTitle}</span>}
              {unitToggleOptions && (
                <UnitToggle
                  options={unitToggleOptions}
                  activeIndex={Math.max(0, unitIndex)}
                  onChange={(idx) => onChange(f.unitKey, unitToggleOptions[idx].value)}
                />
              )}
            </div>
          )}
          {f.label && (
            <div className={styles.filterHeader}>
              <span className={styles.filterLabel}>
                {f.label}
                {f.sublabel && <span className={styles.filterSublabel}> {f.sublabel}</span>}
              </span>
            </div>
          )}
          <RangeInputPair
            minValue={filter[f.minKey || `min${capitalize(f.id)}`]}
            maxValue={filter[f.maxKey || `max${capitalize(f.id)}`]}
            onMinChange={(val) => onChange(f.minKey || `min${capitalize(f.id)}`, val)}
            onMaxChange={(val) => onChange(f.maxKey || `max${capitalize(f.id)}`, val)}
            unit={unit}
            format={format}
            parse={parse}
            convertLabel={f.convertLabel}
            minPlaceholder={f.minPlaceholder || "최소"}
            maxPlaceholder={f.maxPlaceholder || "최대"}
          />
          <RangeSlider
            min={rangeConfig.min}
            max={rangeConfig.max}
            step={rangeConfig.step}
            values={[
              filter[f.minKey || `min${capitalize(f.id)}`],
              filter[f.maxKey || `max${capitalize(f.id)}`],
            ]}
            onFinalChange={(vals) => {
              onChange(f.minKey || `min${capitalize(f.id)}`, vals[0]);
              onChange(f.maxKey || `max${capitalize(f.id)}`, vals[1]);
            }}
            minLabel={f.minLabel || "최소"}
            maxLabel={f.maxLabel || "최대"}
          />
        </div>
        {f.checkbox && (
          <div className={styles.checkboxRow}>
            <button
              type="button"
              className={styles.checkbox}
              data-checked={!!filter[f.checkbox.key]}
              onClick={() => onChange(f.checkbox.key, !filter[f.checkbox.key])}
            >
              <span className={styles.checkboxIcon} data-checked={!!filter[f.checkbox.key]} />
              <span className={`${styles.checkboxLabel} ${filter[f.checkbox.key] ? styles.checkboxLabelActive : ""}`}>{f.checkbox.label}</span>
            </button>
          </div>
        )}
      </>
      );
    }

    case "radio": {
      const currentValue = filter[f.id];
      const isAllSelected = currentValue == null;
      return (
        <div className={styles.radioGroup}>
          {f.showAll && (
            <button
              type="button"
              className={`${styles.radioChip} ${isAllSelected ? styles.radioChipActive : ""}`}
              onClick={() => onChange(f.id, null)}
            >
              <span className={styles.radioIcon} data-checked={isAllSelected} />
              <span>전체</span>
            </button>
          )}
          {f.options.map((opt) => {
            const isSelected = String(currentValue) === String(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={`${styles.radioChip} ${isSelected ? styles.radioChipActive : ""}`}
                onClick={() => onChange(f.id, isSelected ? null : opt.value)}
              >
                <span className={styles.radioIcon} data-checked={isSelected} />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      );
    }

    case "interior-group": {
      const roomValue = filter["roomCnt"] || [];
      const bathroomValue = filter["bathroomCnt"] || [];
      const allEmpty =
        (!roomValue || (Array.isArray(roomValue) && roomValue.length === 0)) &&
        (!bathroomValue || (Array.isArray(bathroomValue) && bathroomValue.length === 0));

      return (
        <div className={styles.interiorGroup}>
          <div className={styles.interiorAllRow}>
            <button
              type="button"
              className={styles.interiorAllBtn}
              data-selected={allEmpty}
              onClick={() => {
                onChange("roomCnt", []);
                onChange("bathroomCnt", []);
              }}
            >
              전체
            </button>
          </div>
          {f.groups.map((group) => {
            const key = group.label === "방수" ? "roomCnt" : "bathroomCnt";
            const value = group.label === "방수" ? roomValue : bathroomValue;
            return (
              <React.Fragment key={group.label}>
                <div className={styles.interiorDivider} />
                <div className={styles.interiorSubgroup}>
                  <div className={styles.interiorSubLabel}>{group.label}</div>
                  <div className={styles.interiorChips}>
                    <ChipGroup
                      options={group.options}
                      value={value}
                      onChange={(val) => onChange(key, val)}
                      multiple
                      showAll
                      size="md"
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      );
    }

    default:
      return null;
  }
}

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResetIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.87972 5.64276L6.78103 4.54407C9.20986 3.1078 12.3926 3.43423 14.4796 5.52127C16.9529 7.99459 16.9529 12.0046 14.4796 14.478C12.0063 16.9513 7.99623 16.9513 5.52291 14.478C4.28592 13.241 3.66773 11.6211 3.66792 9.99888C3.66797 9.58467 3.33222 9.24884 2.918 9.24879C2.50379 9.24875 2.16797 9.5845 2.16792 9.99871C2.16769 12.0026 2.93261 14.009 4.46225 15.5386C7.52136 18.5977 12.4811 18.5977 15.5403 15.5386C18.5994 12.4795 18.5994 7.51971 15.5403 4.46061C12.8636 1.78397 8.73342 1.44982 5.69282 3.45586L4.63881 2.40185C4.42431 2.18735 4.10173 2.12319 3.82147 2.23927C3.54122 2.35536 3.35848 2.62884 3.35848 2.93218L3.35848 6.17309C3.35848 6.372 3.4375 6.56277 3.57815 6.70342C3.71881 6.84407 3.90957 6.92309 4.10848 6.92309H7.34939C7.65274 6.92309 7.92621 6.74036 8.0423 6.4601C8.15838 6.17985 8.09422 5.85726 7.87972 5.64276Z" fill="currentColor" />
  </svg>
);

export default SingleFilterModal;
